/**
 * PRODUCT_DEV-1030: 議事録・商談記録の公開範囲。
 *
 * Unizo の記録は会社内の全員が読めるのが基本（PRODUCT_DEV-996）。重役の商談やプライバシーの
 * 高い商談のために、**必要な人だけが**狭められるようにする（基本設定には置かない）。
 *
 * | 公開範囲 | 見られる人 |
 * |---|---|
 * | `public`（未設定も同じ） | 会社内の全員 |
 * | `limited` | 本人・管理者（records.readAll）・`sharedWith` に入っている人 |
 * | `private` | 本人・管理者 |
 *
 * - 共有された人は**見るだけ**（編集・削除・公開範囲の変更はできない。文字起こし全文も読めない）
 * - 公開範囲を変えられるのは**本人だけ**（管理者も変えられない）
 * - 「誰と共有しているか」（`sharedWith`）は**本人にだけ**返す（`forViewer`）。共有された人・管理者には
 *   公開範囲だけを見せる
 * - 限定・非公開の記録は、多くの人が読む出力（会社AIレポート・グループ要約・部のまとめ・通知・
 *   日報の自動本文・データ出力）に入れない（`isRestrictedRecord`）
 *
 * 見せてよいかの判定は**この1本だけ**を通す（一覧・詳細・検索・顧客ページで述語を書き分けない）。
 */
import { z } from "zod";

export const RECORD_VISIBILITIES = ["public", "limited", "private"] as const;
export const RecordVisibilitySchema = z.enum(RECORD_VISIBILITIES);
export type RecordVisibility = z.infer<typeof RecordVisibilitySchema>;

/** 限定公開で共有できる人数の上限（1件の記録を会社の全員に配る使い方はさせない）。 */
export const SHARED_WITH_MAX = 20;

/** 共有する人の userId の一覧。 */
export const SharedWithSchema = z.array(z.string().min(1).max(128)).max(SHARED_WITH_MAX);

/** 記録に持たせる公開範囲の項目（どちらも省略可。未設定は公開）。 */
export const RecordVisibilityFieldsSchema = z.object({
  visibility: RecordVisibilitySchema.optional(),
  sharedWith: SharedWithSchema.optional(),
});
export type RecordVisibilityFields = z.infer<typeof RecordVisibilityFieldsSchema>;

/**
 * 公開範囲を変える PATCH の本文。⚠ **単独で送る**（ほかの項目と一緒なら 400）。
 * 中身の保存とは別に、本人だけが version を条件にせず保存する。
 */
export const RecordVisibilityPatchSchema = z.object({
  visibility: RecordVisibilitySchema,
  sharedWith: SharedWithSchema.optional(),
});
export type RecordVisibilityPatch = z.infer<typeof RecordVisibilityPatchSchema>;

/** 公開範囲を読む（未設定・不明な値は公開）。 */
export const visibilityOf = (record: { visibility?: string | null }): RecordVisibility =>
  record.visibility === "limited" || record.visibility === "private"
    ? record.visibility
    : "public";

/** 限定公開・非公開か（多くの人が読む出力から外す対象）。 */
export const isRestrictedRecord = (record: { visibility?: string | null }): boolean =>
  visibilityOf(record) !== "public";

/** 公開以外の公開範囲（公開は「何も持たない」で表す）。 */
export type RestrictedVisibility = Exclude<RecordVisibility, "public">;

/** 作る記録へ引き継ぐ・画面の状態に持つ公開範囲（公開なら空）。 */
export interface RestrictedVisibilityFields {
  visibility?: RestrictedVisibility;
  sharedWith?: string[];
}

/**
 * 記録へ引き継ぐ公開範囲をそろえる（公開・不明な値は何も返さない。限定公開で共有する人が空でも
 * 限定公開のまま運ぶ — 選んだ人が後から外れても公開に戻さない）。共有する人の配列は写して返す。
 */
export const restrictedVisibilityOf = (
  visibility: string | null | undefined,
  sharedWith: readonly string[] | null | undefined,
): RestrictedVisibilityFields => {
  if (visibility === "private") return { visibility };
  if (visibility === "limited") return { visibility, sharedWith: [...(sharedWith ?? [])] };
  return {};
};

/**
 * 画面で選んだ公開範囲を、記録の状態へ上書きする形にする（公開は `visibility` も `sharedWith` も
 * undefined にして**消す**。`{ ...prev, ...visibilityStateOf(next) }` の形で使う）。
 */
export const visibilityStateOf = (next: {
  visibility: RecordVisibility;
  sharedWith?: readonly string[];
}): { visibility: RestrictedVisibility | undefined; sharedWith: string[] | undefined } => ({
  visibility: next.visibility === "public" ? undefined : next.visibility,
  sharedWith: next.visibility === "limited" ? [...(next.sharedWith ?? [])] : undefined,
});

/**
 * 応答に載せる形へ絞る: **共有する人（`sharedWith`）は持ち主にだけ返す。**
 * 共有された人・管理者には「誰と共有しているか」を見せない（公開範囲 `visibility` は全員に返す）。
 * @param ownerId 記録の持ち主（商談記録は `createdBy`、議事録は `userId`）
 */
export const forViewer = <T extends { sharedWith?: unknown }>(
  record: T,
  viewerId: string,
  ownerId: string,
): T => {
  if (viewerId === ownerId || !("sharedWith" in record)) return record;
  const { sharedWith: _sharedWith, ...rest } = record;
  return rest as T;
};

export interface RecordViewer {
  /** 見ている人の userId（JWT の sub）。 */
  userId: string;
  /** 管理者（records.readAll）か。 */
  canReadAll: boolean;
}

/**
 * この人にこの記録を見せてよいか。
 * @param ownerId 記録の持ち主（商談記録は `createdBy`、議事録は `userId`）
 */
export const canViewRecord = (
  viewer: RecordViewer,
  record: { visibility?: string | null; sharedWith?: readonly string[] | null },
  ownerId: string,
): boolean => {
  const visibility = visibilityOf(record);
  if (visibility === "public") return true;
  if (viewer.userId === ownerId || viewer.canReadAll) return true;
  return visibility === "limited" && (record.sharedWith ?? []).includes(viewer.userId);
};

/**
 * 保存する形へそろえる。
 * - 公開は属性を持たない（`visibility` も `sharedWith` も消す）
 * - `sharedWith` は限定公開のときだけ残し、重複と本人を除く
 * - 限定公開なのに共有する人が0人なら非公開と同じ（見られるのは本人と管理者）なので、そのまま限定で残す
 */
export const normalizeVisibility = (
  patch: { visibility?: RecordVisibility; sharedWith?: readonly string[] },
  ownerId: string,
): { visibility?: RecordVisibility; sharedWith?: string[] } => {
  const visibility = patch.visibility ?? "public";
  if (visibility === "public") return {};
  if (visibility === "private") return { visibility };
  const sharedWith = [...new Set(patch.sharedWith ?? [])].filter((id) => id !== ownerId);
  return { visibility, sharedWith };
};
